import { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, X, Loader2, Image, Video, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';

const CloudinaryUploader = ({ 
  onUploadComplete, 
  folder = 'bravo',
  resourceType = 'auto', // 'image', 'video', 'auto'
  maxSize = 50 * 1024 * 1024, // 50MB default
  accept = 'image/*,video/*',
  multiple = false,
  className = '',
  buttonText = 'Subir archivo',
  showPreview = true,
  showRequirements = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  const fileInputRef = useRef(null);

  // Supported image formats
  const SUPPORTED_IMAGE_FORMATS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'ico', 'tiff'];
  const SUPPORTED_VIDEO_FORMATS = ['mp4', 'mov', 'avi', 'webm', 'mkv'];

  const getFileExtension = (filename) => {
    return filename.split('.').pop().toLowerCase();
  };

  const isValidFormat = (file) => {
    const ext = getFileExtension(file.name);
    if (file.type.startsWith('image/')) {
      return SUPPORTED_IMAGE_FORMATS.includes(ext);
    }
    if (file.type.startsWith('video/')) {
      return SUPPORTED_VIDEO_FORMATS.includes(ext);
    }
    return false;
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setErrorMsg(null);

    // Validate file format
    const invalidFormatFiles = files.filter(f => !isValidFormat(f));
    if (invalidFormatFiles.length > 0) {
      const ext = getFileExtension(invalidFormatFiles[0].name);
      setErrorMsg(`Formato .${ext} no soportado`);
      toast.error(`Formato .${ext} no soportado. Usá JPG, PNG, GIF, WEBP o SVG`);
      return;
    }

    // Validate file size
    const oversizedFiles = files.filter(f => f.size > maxSize);
    if (oversizedFiles.length > 0) {
      const sizeMB = Math.round(oversizedFiles[0].size / (1024 * 1024));
      setErrorMsg(`Archivo muy grande (${sizeMB}MB)`);
      toast.error(`Archivo muy grande. Máximo ${Math.round(maxSize / (1024 * 1024))}MB`);
      return;
    }

    if (multiple) {
      // Upload multiple files
      for (const file of files) {
        await uploadFile(file);
      }
    } else {
      await uploadFile(files[0]);
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    setProgress(0);

    // Create preview for images
    if (showPreview && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else if (showPreview && file.type.startsWith('video/')) {
      setPreview('video');
    }

    try {
      // Get signature from backend
      const sigResponse = await api.get('/cloudinary/signature', {
        params: {
          folder,
          resource_type: resourceType === 'auto' ? (file.type.startsWith('video/') ? 'video' : 'image') : resourceType
        }
      });

      const { signature, timestamp, cloud_name, api_key } = sigResponse.data;

      // Prepare form data for Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('signature', signature);
      formData.append('timestamp', timestamp);
      formData.append('api_key', api_key);
      formData.append('folder', folder);

      // Determine resource type for upload
      const uploadResourceType = resourceType === 'auto' 
        ? (file.type.startsWith('video/') ? 'video' : 'image')
        : resourceType;

      // Upload to Cloudinary directly
      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud_name}/${uploadResourceType}/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Error al subir archivo');
      }

      const result = await uploadResponse.json();

      // Call callback with result
      if (onUploadComplete) {
        onUploadComplete({
          url: result.secure_url,
          public_id: result.public_id,
          resource_type: result.resource_type,
          format: result.format,
          width: result.width,
          height: result.height,
          duration: result.duration, // For videos
          bytes: result.bytes,
          original_filename: result.original_filename
        });
      }

      toast.success('Archivo subido correctamente');
      setProgress(100);

      // Clear preview after success (optional delay)
      setTimeout(() => {
        setPreview(null);
        setProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.message || 'Error al subir archivo';
      setErrorMsg(errorMessage);
      toast.error(errorMessage);
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const clearPreview = () => {
    setPreview(null);
    setProgress(0);
    setErrorMsg(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        className="hidden"
        data-testid="cloudinary-file-input"
      />

      {/* Upload Button / Drop Zone */}
      {!preview ? (
        <motion.div
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`
            border-2 border-dashed border-zinc-700 hover:border-[#D4AF37] 
            bg-zinc-900/50 hover:bg-zinc-900 
            transition-all cursor-pointer p-6 text-center
            ${uploading ? 'pointer-events-none opacity-50' : ''}
            ${errorMsg ? 'border-red-500/50' : ''}
          `}
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.99 }}
          data-testid="cloudinary-upload-zone"
        >
          {uploading ? (
            <div className="flex flex-col items-center">
              <Loader2 size={32} className="animate-spin text-[#D4AF37] mb-2" />
              <span className="text-sm text-zinc-400">Subiendo...</span>
              {progress > 0 && (
                <div className="w-full max-w-xs mt-3 h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#D4AF37] transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <Upload size={32} className="text-zinc-500 mb-2" />
              <span className="text-sm font-bold text-zinc-300">{buttonText}</span>
              <span className="text-xs text-zinc-500 mt-1">
                Arrastrá o hacé clic para seleccionar
              </span>
              {showRequirements && (
                <span className="text-xs text-zinc-600 mt-2">
                  JPG, PNG, GIF, WEBP • Máx {Math.round(maxSize / (1024 * 1024))}MB
                </span>
              )}
              {errorMsg && (
                <span className="text-xs text-red-400 mt-2">
                  ⚠ {errorMsg}
                </span>
              )}
            </div>
          )}
        </motion.div>
      ) : (
        /* Preview */
        <div className="relative border border-zinc-700 bg-zinc-900 p-2">
          <button
            onClick={clearPreview}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center z-10 hover:bg-red-600 transition-colors"
          >
            <X size={14} />
          </button>

          {preview === 'video' ? (
            <div className="aspect-video bg-zinc-800 flex items-center justify-center">
              <Video size={48} className="text-zinc-500" />
              <span className="ml-2 text-zinc-400">Video</span>
            </div>
          ) : (
            <div className="relative">
              <img
                src={preview}
                alt="Preview"
                className="w-full h-auto max-h-48 object-contain"
              />
              {progress === 100 && (
                <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
                  <CheckCircle2 size={48} className="text-green-500" />
                </div>
              )}
            </div>
          )}

          {uploading && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800">
              <div 
                className="h-full bg-[#D4AF37] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CloudinaryUploader;
